import React, { useState } from 'react';
import FileViewer from '../components/FileViewer';
import ImageViewer from '../components/ImageViewer';
import PDFViewer from '../components/PDFViewer';
import AttachmentUploader from '../components/AttachmentUploader';

const FileViewerTestPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
  const [testFiles, setTestFiles] = useState([]);

  const sampleFiles = [
    {
      filename: 'sample-landscape.jpg',
      url: 'https://picsum.photos/800/600?random=1',
      size: '250 KB',
      type: 'image'
    },
    {
      filename: 'sample-portrait.jpg',
      url: 'https://picsum.photos/600/800?random=2',
      size: '320 KB',
      type: 'image'
    },
    {
      filename: 'sample-document.pdf',
      url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
      size: '13 KB',
      type: 'pdf'
    }
  ];

  const handleFileView = (file, viewerType) => {
    setSelectedFile(file);
    if (viewerType === 'unified') {
      setIsFileViewerOpen(true);
    } else if (viewerType === 'image') {
      setIsImageViewerOpen(true);
    } else if (viewerType === 'pdf') {
      setIsPDFViewerOpen(true);
    }
  };

  const closeAllViewers = () => {
    setIsFileViewerOpen(false);
    setIsImageViewerOpen(false);
    setIsPDFViewerOpen(false);
    setSelectedFile(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-primary mb-4">File Viewer Components Test</h1>
        <p className="text-gray-600">Test the image and PDF viewer components with sample files</p>
      </div>

      {/* Attachment Uploader Test */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold text-primary mb-4">1. Attachment Uploader Component</h2>
        <p className="text-gray-600 mb-4">
          Upload your own files to test the attachment uploader with preview functionality:
        </p>
        <AttachmentUploader
          files={testFiles}
          setFiles={setTestFiles}
          maxFiles={3}
          maxSize={10485760}
        />
      </div>

      {/* Sample Files Test */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold text-primary mb-4">2. File Viewer with Sample Files</h2>
        <p className="text-gray-600 mb-4">
          Click on the files below to test the different viewer components:
        </p>

        <div className="space-y-4">
          {sampleFiles.map((file, index) => (
            <div key={index} className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">
                  {file.type === 'image' ? '🖼️' : '📄'}
                </span>
                <div>
                  <h3 className="font-medium text-gray-900">{file.filename}</h3>
                  <p className="text-sm text-gray-500">{file.size}</p>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleFileView(file, 'unified')}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
                >
                  Open with FileViewer
                </button>

                {file.type === 'image' && (
                  <button
                    onClick={() => handleFileView(file, 'image')}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                  >
                    Open with ImageViewer
                  </button>
                )}

                {file.type === 'pdf' && (
                  <button
                    onClick={() => handleFileView(file, 'pdf')}
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                  >
                    Open with PDFViewer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
        <h2 className="text-lg font-bold text-yellow-800 mb-2">📋 Test Instructions</h2>
        <ul className="list-disc list-inside text-yellow-700 space-y-1">
          <li>Upload files using the drag-and-drop area above</li>
          <li>Click on uploaded files to preview them</li>
          <li>Test the sample files with different viewer components</li>
          <li>Try downloading files from the viewers</li>
          <li>Test opening PDFs in new tabs</li>
          <li>Test the close functionality and escape key</li>
        </ul>
      </div>

      {/* Navigation */}
      <div className="text-center space-x-4">
        <a href="/" className="text-blue-600 hover:text-blue-800 underline">
          ← Back to Home
        </a>
        <a href="/submit" className="text-blue-600 hover:text-blue-800 underline">
          Test Report Form
        </a>
        <a href="/dashboard" className="text-blue-600 hover:text-blue-800 underline">
          Test Dashboard
        </a>
      </div>

      {/* File Viewers */}
      <FileViewer
        file={selectedFile}
        isOpen={isFileViewerOpen}
        onClose={closeAllViewers}
      />

      <ImageViewer
        isOpen={isImageViewerOpen}
        onClose={closeAllViewers}
        imageUrl={selectedFile?.url}
        filename={selectedFile?.filename}
      />

      <PDFViewer
        isOpen={isPDFViewerOpen}
        onClose={closeAllViewers}
        pdfUrl={selectedFile?.url}
        filename={selectedFile?.filename}
      />
    </div>
  );
};

export default FileViewerTestPage;
